import AssignmentIcon from '@mui/icons-material/Assignment';
import CompareIcon from '@mui/icons-material/Compare';
import { Box, Paper, Stack, Typography } from '@mui/material';
import {
  IS_UXBEAUTY,
  UserStateContextType
} from 'components/constants/Constants';

import { useContext } from 'react';
import { legacyBreakpoints } from 'theme/legacyThemeVariables';
import { tss } from 'tss-react/mui';
import { ProductType } from 'types/Products';
import { SegmentTag } from 'types/global';
import SearchBar from '../SearchBar/SearchBar';
import { SearchHeaderButton } from './SearchHeaderButton';
import {
  SearchResultMode,
  SearchResultModeToggle
} from './SearchResultModeToggle';

import { SearchContext } from 'context/SearchContext';
import { UxBeautyFilters } from './UxBeautyFilters';
import { CovaloMobileFilters } from './CovaloMobileFilters';
import { useSticky } from 'hooks/useOnSticky';
import useNavbarHeight from 'hooks/useNavBarHeight';
import { useParams } from 'react-router-dom';
import { useUserState } from 'context/User';
import {
  LightTooltip,
  NeedMoreTooltip
} from 'components/pages/product/variants/UXB/Sections/Requests/LightToolTip';
import { AdminHeaderCompanyLogo } from 'components/pages/BrandPortal/Users/AdminPagesHeader';
import { UnionCustomAttributeProps } from 'components/common/Portals/CustomAttributes/types';
import { SearchTarget } from 'types/Search';

type SearchHeaderProps = {
  target: SearchTarget;
  segment: SegmentTag;
  onSegmentChange: (value: SegmentTag) => void;
  onQueryChange: (value: string) => void;
  bookmarksEnabled: boolean;
  onToggleBookmarks: () => void;
  defaultSearchRequestQuery: string;
  onRequestButtonClick: () => void;
  onCompareButtonClick: () => void;
  compareModeActive: boolean;
  requestsModeActive: boolean;
  isBrandPortal: boolean;
  searchResultMode: SearchResultMode;
  onSearchResultModeChange: (mode: SearchResultMode) => void;
  /**
   * @deprecated Will be removed after LaPrairie migration
   */
  isLaPrairieEvent: boolean;
  companyAttributeDefinitions?: UnionCustomAttributeProps[];
  productAttributeDefinitions?: UnionCustomAttributeProps[];
  placeholder: string;
};

export const SearchHeader: React.FC<SearchHeaderProps> = props => {
  const { classes } = useStyles();
  const { event: eventKey } = useParams();
  const userState = useUserState();
  const {
    request: searchRequest,
    clearAllFilters,
    results: searchResults
  } = useContext(SearchContext);
  const navHeight = useNavbarHeight();
  const { ref, isSticky } = useSticky(navHeight);
  const handleSubmit = (event: MouseEvent, currentValue: string) => {
    event.preventDefault();
    props.onQueryChange(currentValue);
  };

  // --------- new code for filters

  return (
    <Box
      className={classes.header}
      ref={ref}
      sx={theme => ({
        boxShadow: isSticky ? theme.shadows[2] : ''
      })}
    >
      {!IS_UXBEAUTY &&
        props.isBrandPortal &&
        props.target === ProductType.INGREDIENTS && (
          <div className={classes.logoContainer}>
            <AdminHeaderCompanyLogo event={eventKey} />
          </div>
        )}
      <Paper sx={{ padding: IS_UXBEAUTY ? 2 : 0, width: '100%' }}>
        <div className={classes.searchContainer}>
          <div className={classes.searchBarContainer}>
            <SearchBar
              target={props.target}
              onSubmit={handleSubmit}
              placeholder={props.placeholder}
              segment={props.segment}
              onSegmentChange={props.onSegmentChange}
              bookmarksEnabled={props.bookmarksEnabled}
              onToggleBookmarks={props.onToggleBookmarks}
              defaultSearchValue={props.defaultSearchRequestQuery}
            />
          </div>

          <div className={classes.searchHeaderButtons}>
            {IS_UXBEAUTY && (
              <Typography
                sx={theme => ({
                  color: theme.palette.primary.light,
                  '&': { fontWeight: theme.typography.h5.fontWeight }
                })}
              >
                {searchResults.total > 0
                  ? `${searchResults.total} result${
                      searchResults.total > 1 ? 's' : ''
                    }`
                  : 'No results'}
              </Typography>
            )}
            <Stack
              sx={{
                flexDirection: 'row',
                alignContent: 'flex-end',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: 'end'
              }}
            >
              {props.target !== 'SUPPLIERS' ? (
                <LightTooltip
                  color={
                    userState.type === UserStateContextType.AUTHENTICATED
                      ? 'black'
                      : undefined
                  }
                  title={
                    userState.type === UserStateContextType.AUTHENTICATED ? (
                      props.requestsModeActive ? (
                        'End Request mode'
                      ) : (
                        'Start Request mode'
                      )
                    ) : (
                      <NeedMoreTooltip
                        customText={
                          'Please log in to be able to perform requests'
                        }
                      />
                    )
                  }
                >
                  <div className={classes.searchHeaderButtonContainer}>
                    <SearchHeaderButton
                      Icon={AssignmentIcon}
                      onClick={props.onRequestButtonClick}
                      active={props.requestsModeActive}
                      className={classes.searchHeaderButton}
                      variant={!IS_UXBEAUTY ? 'outlined' : 'contained'}
                      onMouseDown={e => {
                        if (
                          userState.type !== UserStateContextType.AUTHENTICATED
                        ) {
                          e.preventDefault();
                        }
                      }}
                    >
                      Requests
                    </SearchHeaderButton>
                  </div>
                </LightTooltip>
              ) : null}
              {props.target === ProductType.INGREDIENTS && (
                <LightTooltip
                  color={
                    userState.type === UserStateContextType.AUTHENTICATED
                      ? 'black'
                      : undefined
                  }
                  title={
                    userState.type === UserStateContextType.AUTHENTICATED ? (
                      props.compareModeActive ? (
                        'End compare mode'
                      ) : (
                        'Start compare mode to view details for up to four ingredients side by side (max: 4)'
                      )
                    ) : (
                      <NeedMoreTooltip
                        customText={
                          'Please log in to be able to compare ingredients side by side'
                        }
                      />
                    )
                  }
                >
                  <div className={classes.searchHeaderButtonContainer}>
                    <SearchHeaderButton
                      onClick={props.onCompareButtonClick}
                      Icon={CompareIcon}
                      active={props.compareModeActive}
                      className={classes.searchHeaderButton}
                      variant={!IS_UXBEAUTY ? 'outlined' : 'contained'}
                      //onMouseDown is to prevent undesired focus effects on the tooltip, we still trigger the handle function onClick
                      onMouseDown={e => {
                        userState.type !== UserStateContextType.AUTHENTICATED &&
                          e.preventDefault();
                      }}
                    >
                      Compare
                    </SearchHeaderButton>
                  </div>
                </LightTooltip>
              )}

              {IS_UXBEAUTY && (
                <div>
                  <SearchResultModeToggle
                    mode={props.searchResultMode}
                    onModeChange={props.onSearchResultModeChange}
                  />
                </div>
              )}
            </Stack>
          </div>
        </div>
        {IS_UXBEAUTY && props.target !== 'SUPPLIERS' ? (
          <UxBeautyFilters
            searchRequest={searchRequest}
            productType={props.target as ProductType}
          />
        ) : (
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              justifyContent: 'start',
              p: 2,
              pt: 0,
              gap: 1
            }}
          >
            <CovaloMobileFilters
              isLaPrairieEvent={props.isLaPrairieEvent}
              clearAllFilters={clearAllFilters}
              searchRequest={searchRequest}
              isBrandPortal={props.isBrandPortal}
              companyAttributeDefinitions={props.companyAttributeDefinitions}
              productAttributeDefinitions={props.productAttributeDefinitions}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// searchbar: layout mid screen size private

const useStyles = tss.create(({ theme }) => ({
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    gap: '16px',
    marginBottom: theme.spacing(2),
    zIndex: 3,

    [legacyBreakpoints.down('md')]: {
      flexDirection: 'column'
    }
  },
  logoContainer: {
    background: 'white',
    width: 270,
    flexShrink: 0,
    position: 'relative',
    display: 'grid',
    placeContent: 'center',

    [legacyBreakpoints.down('md')]: {
      minHeight: 48,
      width: '100%'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  searchContainer: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'row',
    [legacyBreakpoints.down('lg')]: {
      flexDirection: 'row',
      gap: theme.spacing(1)
    },
    [legacyBreakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-end'
    }
  },

  searchHeaderButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    flexGrow: IS_UXBEAUTY ? 1 : undefined,
    justifyContent: 'space-between',
    alignItems: 'center',
    [legacyBreakpoints.down('sm')]: {
      flexWrap: 'wrap'
    }
  },

  searchHeaderButtonContainer: {
    [legacyBreakpoints.down('md')]: {
      flexGrow: 1,
      display: 'flex'
    }
  },

  searchHeaderButton: {
    [legacyBreakpoints.down('md')]: {
      flexGrow: 1
    },

    '&.btn:disabled': {
      color: 'var(--falcon-primary) !important',
      opacity: 0.5,
      border: '1px solid var(--falcon-primary) !important'
    }
  },

  searchBarContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    gap: 2,
    minWidth: '50%',
    [legacyBreakpoints.down('md')]: {
      width: '100%',
      maxWidth: 'unset'
    }
  }
}));
